/* eslint-disable no-restricted-syntax */
import PropTypes from 'prop-types';
import moment from 'moment';
import { useParams } from 'react-router';
import { useImperativeHandle, forwardRef, useEffect, useState, useCallback, useRef } from 'react';
// Google map autocomplete
import Autocomplete from 'src/components/AddressAutocomplete';
// form
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from '@mui/material/styles';
// @mui
import {
    Box,
    Grid,
    Typography,
    Checkbox,
    MenuItem,
    Divider,
    FormControl,
    FormHelperText,
    FormGroup,
    FormControlLabel,
    ToggleButtonGroup,
    ToggleButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
// components
import { piiProps } from 'src/components/DemoBlur';
import { Iconify } from '.';
import { ReferralDropdown } from './Dropdowns';
import MarketingPreferences from '../sections/@dashboard/leadDetail/MarketingPreferences';
import DentallyPatientLinkSection from './dentally/DentallyPatientLinkSection';
import DentallyLeadSyncSection from './dentally/DentallyLeadSyncSection';
import { useDentallyLeadContext } from '../contexts/DentallyLeadContext';
import useDentallyLeadSync from '../hooks/useDentallyLeadSync';
import { differenceLabel, joinLabels } from '../utils/dentallyLeadSyncPresentation';
// hook-form
import { FormProvider, RHFTextField, RHFSelect } from './hook-form';
// @store
import { dispatch, useSelector } from '../redux/store';
import { savePersonalDetails, updatePersonalDetail, followUpSlice } from '../redux/slices/lead';
// style
import {
    boxStyle,
    menuItemStyle,
    fieldBoxStyle,
    controllerBoxStyle,
    autocompleteBoxStyle,
    selectBoxStyle,
    selectStyle
} from '../styles/PersonalDetailsStyle';
// validation
import { PersonalDetailSchema } from '../validations/validations';
// constants
import {
    inputKeys
} from '../constants/LeadDetailConst';
import { toggleButtonStyle } from '../styles/Common';

const DAYS_OF_WEEK = [
    { label: "MON", value: "MONDAY" },
    { label: "TUE", value: "TUESDAY" },
    { label: "WED", value: "WEDNESDAY" },
    { label: "THR", value: "THURSDAY" },
    { label: "FRI", value: "FRIDAY" },
    { label: "SAT", value: "SATURDAY" },
    { label: "SUN", value: "SUNDAY" }
]

const CONTACT_PERIOD = [
    { label: "Afternoon", value: "AFTERNOON" },
    { label: "Evening", value: "EVENING" },
    { label: "Morning", value: "MORNING" },
]

const LeadDetailsPersonalDetails = forwardRef(({ handleNext, type }, ref) => {
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';
    const { id } = useParams();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    // Dentally scope + shared patient link from LeadDetailViewBody (null outside that route).
    const dentallyLead = useDentallyLeadContext();
    const { leadDetail } = useSelector((state) => state.lead);
    // One /dentally/sync/ read for both children. The differences section owns
    // the comparison, the patient card owns the read-only provider facts
    // (patient_summary) and the last-compared time, and they must not fetch it
    // twice. Same enabling condition the differences section already had — a
    // uniquely linked lead — so an unlinked lead still costs nothing.
    const dentallySyncEnabled = Boolean(
        dentallyLead?.scope?.visible
        && dentallyLead?.patientLink?.outcome === 'unique'
        && leadDetail?.id
        && practiceId,
    );
    const dentallySync = useDentallyLeadSync(
        dentallySyncEnabled ? practiceId : null,
        dentallySyncEnabled ? leadDetail?.id : null,
    );
    const { preferredContactMethodData, referral, sourceType, salutationOptions } = useSelector((state) => state.schema);

    const referralValue = (value, data) => referral?.length > 0 && referral?.filter((res) => res[value] === data.referral);

    const defaultValues = {
        firstName: leadDetail?.first_name || '',
        lastName: leadDetail?.last_name || '',
        title: leadDetail?.salutation || '',
        email: leadDetail?.lead_contact?.email || '',
        notes: leadDetail?.lead_contact?.note || '',
        phoneNumber: leadDetail?.lead_contact?.phone || '',
        dob: leadDetail && leadDetail?.dob && new Date(moment(leadDetail?.dob, 'DD-MM-YYYY')) || null,
        preferredContactMethod: leadDetail && leadDetail?.lead_contact && leadDetail?.lead_contact?.preferred_contact_method || '',
        preferredContactPeriods: leadDetail?.lead_contact?.preferred_contact_periods || [],
        preferredContactDays: leadDetail?.lead_contact?.preferred_contact_days || [],
        address_line_1: leadDetail && leadDetail?.lead_address && leadDetail?.lead_address?.address_line_1 || '',
        county: leadDetail && leadDetail?.lead_address && leadDetail?.lead_address?.county || '',
        city: leadDetail && leadDetail?.lead_address && leadDetail?.lead_address?.city || '',
        postcode: leadDetail && leadDetail?.lead_address && leadDetail?.lead_address?.postcode || '',
        address_line_2: leadDetail && leadDetail?.lead_address && leadDetail?.lead_address?.address_line_2 || '',
        address: '',
        type: leadDetail && leadDetail?.lead_source && leadDetail?.lead_source.type || '',
        referral: leadDetail && leadDetail?.lead_source && leadDetail?.lead_source?.referral || '',
        isRegister: leadDetail && leadDetail?.is_registered_patient || false,
        sex: leadDetail?.sex || '',
        nhs_number: leadDetail?.nhs_number || '',
        emergencyContactName: leadDetail?.emergency_contact_name || '',
        emergencyContactPhone: leadDetail?.emergency_contact_phone || '',
    };

    const methods = useForm({
        resolver: yupResolver(PersonalDetailSchema),
        defaultValues,
    });

    const {
        setValue,
        handleSubmit,
        watch,
        control
    } = methods;

    const values = watch();

    const onSubmit = async (data) => {
        const detail = {
            salutation: data.title && data.title.toUpperCase() || '',
            first_name: data.firstName,
            last_name: data.lastName || '',
            dob: data.dob ? moment(data.dob, 'DD/MM/YYYY').format('DD-MM-YYYY') : null,
            is_registered_patient: data.isRegister,
            lead_contact: {
                email: data.email,
                phone: data.phoneNumber
            },
            lead_source: {}
        };
        if (data.notes) {
            detail.lead_contact.note = data.notes;
        }
        if (data) {
            detail.lead_address = {
                ...detail.lead_address,
                address_line_1: data.address_line_1
            };
        }
        if (data) {
            detail.lead_address = {
                ...detail.lead_address,
                postcode: data.postcode
            };
        }
        if (data) {
            detail.lead_address = {
                ...detail.lead_address,
                address_line_2: data.address_line_2
            };
        }
        if (data) {
            detail.lead_address = {
                ...detail.lead_address,
                city: data.city,
            };
        }
        if (data) {
            detail.lead_address = {
                ...detail.lead_address,
                county: data.county
            };
        }
        if (data.preferredContactMethod) {
            detail.lead_contact.preferred_contact_method = data.preferredContactMethod.toUpperCase();
        }
        if (data.preferredContactDays) {
            detail.lead_contact.preferred_contact_days = data.preferredContactDays || [];
        }
        if (data.preferredContactPeriods) {
            detail.lead_contact.preferred_contact_periods = data.preferredContactPeriods || [];
        }
        if (data.type) {
            detail.lead_source = {
                ...detail.lead_source,
                type: data.type
            };
        }
        if (data.referral) {
            detail.lead_source = {
                ...detail.lead_source,
                referral: data.referral
            };
        }
        if (data.affiliate_user) {
            detail.lead_source = {
                ...detail.lead_source,
                affiliate_user: data.affiliate_user,
                type: "CLINICIAN"
            };
        }
        if (data.sex) {
            detail.sex = data.sex;
        }
        if (data.nhs_number) {
            detail.nhs_number = data.nhs_number;
        }
        if (data.emergencyContactName) {
            detail.emergency_contact_name = data.emergencyContactName;
        }
        if (data.emergencyContactPhone) {
            detail.emergency_contact_phone = data.emergencyContactPhone;
        }
        const updateForm = JSON.stringify(defaultValues) === JSON.stringify(data);
        if (leadDetail && leadDetail.id) {
            if (!updateForm) {
                detail.id = leadDetail.id;
                dispatch(updatePersonalDetail(detail, practiceId, handleNextWithPush, 'leadDetail'));
            } else {
                handleNext(true);
            }
        }
        else {
            dispatch(savePersonalDetails(detail, practiceId, handleNextWithPush));
        }
    };

    // Identity fields the operator has been asked to confirm, or null.
    const [identityConfirm, setIdentityConfirm] = useState(null);

    /**
     * Send the saved details on to Dentally.
     *
     * Deliberately NOT awaited by the save: the CRM write is the operation and
     * must never appear to fail because Dentally is slow or unreachable. A
     * failed push leaves the row PENDING server-side and the sweeper retries
     * it, so the worst case is "not yet", never "lost".
     *
     * Contact fields go silently — the operator typed them, confirming what
     * they just typed buys nothing. A name or date of birth comes back as
     * `identityPending` instead, because those are what Dentally matches
     * patients on and changing one can break the link or create a duplicate.
     */
    // Read the sync through a ref, never the closure.
    //
    // `submit()` below is registered with useImperativeHandle and an EMPTY
    // dependency array, so the parent's Save button calls an `onSubmit`
    // captured on the FIRST render — and on that render the /dentally/sync/
    // read has not come back, so `linked` is still false. Closing over
    // `dentallySync` meant the guard here always saw that initial false and
    // the push never fired: confirmed on staging, where saving a changed
    // surname produced no POST to /dentally/push/ at all.
    //
    // The consequence was worse than a missing dialog. The server-side sweeper
    // deliberately never sends identity fields, so a changed name or date of
    // birth reached nothing and nobody was told — the row converged to SYNCED
    // with the old name still in Dentally.
    //
    // Same fix as `dentallyRequiredRef` in PersonalDetails.jsx, for the same
    // reason: a value a frozen callback depends on must be read at call time.
    const dentallySyncRef = useRef(dentallySync);
    dentallySyncRef.current = dentallySync;

    const pushDetailsToDentally = useCallback(async () => {
        const sync = dentallySyncRef.current;
        if (!sync?.enabled || !sync?.linked) return;
        const result = await sync.push();
        if (result.identityPending.length > 0) {
            setIdentityConfirm(result.identityPending);
        } else if (result.conflict) {
            // The section below renders the conflict; re-read so it has the
            // differences to show.
            sync.refresh();
        }
    }, []);

    const handleNextWithPush = useCallback((...args) => {
        pushDetailsToDentally();
        return handleNext?.(...args);
    }, [pushDetailsToDentally, handleNext]);

    const confirmIdentityPush = async () => {
        setIdentityConfirm(null);
        const result = await dentallySync.push({ confirmIdentity: true });
        if (result.conflict) dentallySync.refresh();
    };

    const setAddressData = (addresses, fullAddress) => {
        ClearAddressData();
        if (addresses && addresses.length > 0) {
            const address1 = fullAddress.split(',')[0];
            setValue('address_line_1', address1);
            addresses.forEach(address => {
                if (address.types.indexOf('postal_code') > -1) {
                    setValue('postcode', address.long_name);
                }
                if (address.types.indexOf('country') > -1) {
                    if (address1 === address.long_name) {
                        setValue('address_line_1', '');
                    }
                    setValue('county', address.long_name);
                }
                if (address.types.indexOf('locality') > -1 || address.types.indexOf('postal_town') > -1) {
                    if (address1.includes(address.long_name)) {
                        setValue('address_line_1', '');
                    }
                    setValue('city', address.long_name);
                }
                if (address.types.indexOf('route') > -1) {
                    if (!address1.includes(address.long_name)) {
                        setValue('address_line_2', address.long_name);
                    } else {
                        setValue('address_line_2', '');
                    }
                }
            });
        } else {
            ClearAddressData();
        }
    };

    const ClearAddressData = () => {
        setValue('postcode', '');
        setValue('address_line_2', '');
        setValue('county', '');
        setValue('city', '');
        setValue('address_line_1', '');
    };

    useImperativeHandle(
        ref,
        () => ({
            async submit() {
                await handleSubmit(onSubmit)();
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleChange = (event, data) => {
        const {
            target: { value },
        } = event;
        setValue(data, value);
    };

    const handleReferral = (event, data) => {
        if (data) {
            setValue('referral', data?.id ? "CLINICIAN" : data?.value);
            if (data?.id) {
                setValue('affiliate_user', data?.id);
                setValue('type', "CLINICIAN");
            }
        }
    };

    useEffect(() => {
        if (leadDetail.id !== Number(id)) {
            dispatch(followUpSlice(id, practiceId, 'LeadDetail'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadDetail, id]);

    // After copying Dentally values into the CRM, refetch the lead AND push the new
    // values into this form.
    //
    // Refetching alone is not enough: `defaultValues` is read once when the form is
    // created, so updating `leadDetail` in Redux leaves the visible inputs showing the
    // old value even though the CRM has the new one. Only the fields that were actually
    // applied are written, so an unsaved edit elsewhere in the form survives.
    const setAppliedFieldValues = (fresh, fields) => {
        if (!fresh) return;
        const setters = {
            first_name: () => setValue('firstName', fresh.first_name || ''),
            last_name: () => setValue('lastName', fresh.last_name || ''),
            email: () => setValue('email', fresh.lead_contact?.email || ''),
            phone: () => setValue('phoneNumber', fresh.lead_contact?.phone || ''),
            dob: () => setValue('dob', fresh.dob ? new Date(moment(fresh.dob, 'DD-MM-YYYY')) : null),
            address: () => {
                setValue('address_line_1', fresh.lead_address?.address_line_1 || '');
                setValue('address_line_2', fresh.lead_address?.address_line_2 || '');
                setValue('city', fresh.lead_address?.city || '');
                setValue('county', fresh.lead_address?.county || '');
                setValue('postcode', fresh.lead_address?.postcode || '');
            },
        };
        fields.forEach((field) => setters[field]?.());
    };

    const handleDentallySyncApplied = (appliedFields = []) => {
        if (!leadDetail?.id || !practiceId) return;
        dispatch(followUpSlice(
            leadDetail.id,
            practiceId,
            'LeadDetail',
            (fresh) => setAppliedFieldValues(fresh, appliedFields),
        ));
    };

    const getAddressField = () => {
        if (values.address_line_1 || values.address_line_2 || values.city || values.county || values.postcode) {
            return true;
        }
        return false;
    };

    return (
        <FormProvider methods={methods}>
            <Grid container spacing={1}>
                <Grid item xs={12} md={12}>
                    <Box>
                        <Typography variant="h6" sx={{ marginBottom: '15px' }}>
                            {type === 'followUp' ? 'Patient Details' : 'Personal Details'}
                        </Typography>
                        <Divider sx={{ marginBottom: '30px' }} />
                        {dentallyLead?.scope?.visible && dentallyLead?.patientLink && (
                            <DentallyPatientLinkSection
                                practiceId={practiceId}
                                leadId={leadDetail?.id}
                                link={dentallyLead.patientLink.link}
                                outcome={dentallyLead.patientLink.outcome}
                                isLoading={dentallyLead.patientLink.isLoading}
                                canManage={Boolean(dentallyLead.scope.canManage)}
                                onRefresh={dentallyLead.patientLink.refresh}
                                summary={dentallySync.patientSummary}
                                checkedAt={dentallySync.checkedAt}
                            />
                        )}
                        {/* Differences against the linked Dentally patient. Only meaningful once
                            a single patient link is known, so it is mounted (and only then does
                            it fetch) when the lookup resolved to exactly one patient. */}
                        {dentallyLead?.scope?.visible && dentallyLead?.patientLink?.outcome === 'unique' && leadDetail?.id && (
                            <DentallyLeadSyncSection
                                sync={dentallySync}
                                practiceId={practiceId}
                                leadId={leadDetail.id}
                                onApplied={handleDentallySyncApplied}
                            />
                        )}
                        {/* Identity confirmation. Contact details sync silently;
                            a name or date of birth asks first, because those are
                            the fields Dentally matches patients on — changing one
                            can break the link to this patient or leave the
                            practice with a duplicate record. */}
                        <Dialog
                            open={Boolean(identityConfirm)}
                            onClose={() => setIdentityConfirm(null)}
                            maxWidth="xs"
                            fullWidth
                        >
                            <DialogTitle>Update this in Dentally too?</DialogTitle>
                            <DialogContent>
                                <Typography variant="body2" color="text.secondary">
                                    You changed the{' '}
                                    {joinLabels((identityConfirm || []).map(
                                        (field) => differenceLabel({ field }).toLowerCase()
                                    ))}
                                    . The rest of this lead&rsquo;s details have been sent to Dentally
                                    already; this one identifies the patient, so it needs your say-so.
                                </Typography>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    size="small"
                                    color="inherit"
                                    onClick={() => setIdentityConfirm(null)}
                                >
                                    Keep Dentally as it is
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={confirmIdentityPush}
                                    disabled={dentallySync?.isPushing}
                                >
                                    {dentallySync?.isPushing ? 'Updating…' : 'Update Dentally'}
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <Box sx={boxStyle}>
                            <RHFSelect
                                name={'title'}
                                label="Title"
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                            >
                                {salutationOptions?.map((option, index) => (
                                    <MenuItem
                                        key={index}
                                        value={option.value}
                                        sx={menuItemStyle}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </RHFSelect>
                            <RHFTextField name="firstName" label="First name (Required)" {...piiProps} />
                            <RHFTextField name="lastName" label="Last name" {...piiProps} />
                            <Box>
                                <Controller
                                    name="dob"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <DesktopDatePicker
                                            orientation="landscape"
                                            label="Date of birth"
                                            value={field.value || null}
                                            format="dd/MM/yyyy"
                                            maxDate={new Date()}
                                            onChange={(newValue) => {
                                                field.onChange(newValue);
                                            }}
                                            slotProps={{
                                                textField: {
                                                    ...piiProps,
                                                    fullWidth: true,
                                                    error: !!error,
                                                    helperText: error?.message,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                            <RHFSelect
                                name="sex"
                                label="Gender"
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                            >
                                {['Male', 'Female', 'Unknown'].map((g) => (
                                    <MenuItem key={g} value={g} sx={menuItemStyle}>{g}</MenuItem>
                                ))}
                            </RHFSelect>
                        </Box>
                        <Box sx={fieldBoxStyle}>
                            <RHFTextField name='email' label='Email' {...piiProps} />
                            <RHFTextField
                                name='phoneNumber'
                                {...piiProps}
                                type='string'
                                inputProps={{ maxLength: 15 }}
                                label='Phone number (Required)'
                                onKeyDown={(evt) => {
                                    const isControlC = (evt.ctrlKey && evt.key === 'c') || (evt.metaKey && evt.key === 'c');
                                    const isControlV = (evt.ctrlKey && evt.key === 'v') || (evt.metaKey && evt.key === 'v');
                                    const isAllowedKey = inputKeys.includes(evt.key) || (/^[0-9]*$/).test(evt.key);
                                    if (!(isControlC || isControlV || isAllowedKey)) {
                                        evt.preventDefault();
                                    }
                                }}
                            />
                        </Box>
                        <Box sx={controllerBoxStyle}>
                            <Controller
                                name="preferredContactMethod"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <FormControl>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ m: '0 0 10px' }}
                                        >
                                            Preferred Contact Method
                                        </Typography>
                                        <ToggleButtonGroup
                                            color="primary"
                                            exclusive
                                            value={field.value}
                                            onChange={(e, value) => {
                                                if (value === null) {
                                                    field.onChange(field.value);
                                                    return;
                                                }
                                                field.onChange(value);
                                            }}
                                            aria-label="Platform"
                                            sx={{ borderRadius: 0, border: 'none', flexWrap: 'wrap' }}
                                            error={error && !field.value?.length}
                                        >
                                            {preferredContactMethodData?.map((options, index) => (
                                                <ToggleButton
                                                    key={index}
                                                    value={options.value}
                                                    sx={toggleButtonStyle(isLight)}
                                                >
                                                    <Iconify icon={field.value === options.value ? 'bi:check-circle-fill' : 'material-symbols:circle'} width={20} height={20} sx={{ mr: 1, color: field.value === options.value ? '#00A76F' : '#C4CDD5' }} />{options.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                        {error && !field.value?.length &&
                                            <FormHelperText error sx={{ px: 2, textTransform: 'capitalize' }}>
                                                {error?.message}
                                            </FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Box>
                        <Box sx={controllerBoxStyle}>
                            <Controller
                                name="preferredContactDays"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <FormControl>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ m: '0 0 10px' }}
                                        >
                                            Preferred Contact Days
                                        </Typography>
                                        <ToggleButtonGroup
                                            color="primary"
                                            value={field.value}
                                            onChange={(e, value) => {
                                                field.onChange(value);
                                            }}
                                            aria-label="Platform"
                                            sx={{ borderRadius: 0, border: 'none', flexWrap: 'wrap' }}
                                            error={error && !field.value?.length}
                                        >
                                            {DAYS_OF_WEEK?.map((options, index) => (
                                                <ToggleButton
                                                    key={index}
                                                    value={options.value}
                                                    sx={toggleButtonStyle(isLight)}
                                                >
                                                    <Iconify icon={field.value?.includes(options.value) ? 'bi:check-circle-fill' : 'material-symbols:circle'} width={20} height={20} sx={{ mr: 1, color: field.value?.includes(options.value) ? '#00A76F' : '#C4CDD5' }} />{options.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                        {error && !field.value?.length &&
                                            <FormHelperText error sx={{ px: 2, textTransform: 'capitalize' }}>
                                                {error?.message}
                                            </FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Box>
                        <Box sx={controllerBoxStyle}>
                            <Controller
                                name="preferredContactPeriods"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <FormControl>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ m: '0 0 10px' }}
                                        >
                                            Preferred Contact Periods
                                        </Typography>
                                        <ToggleButtonGroup
                                            color="primary"
                                            value={field.value}
                                            onChange={(e, value) => {
                                                field.onChange(value);
                                            }}
                                            aria-label="Platform"
                                            sx={{ borderRadius: 0, border: 'none', flexWrap: 'wrap' }}
                                            error={error && !field.value?.length}
                                        >
                                            {CONTACT_PERIOD?.map((options, index) => (
                                                <ToggleButton
                                                    key={index}
                                                    value={options.value}
                                                    sx={toggleButtonStyle(isLight)}
                                                >
                                                    <Iconify icon={field.value?.includes(options.value) ? 'bi:check-circle-fill' : 'material-symbols:circle'} width={20} height={20} sx={{ mr: 1, color: field.value?.includes(options.value) ? '#00A76F' : '#C4CDD5' }} />{options.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                        {error && !field.value?.length &&
                                            <FormHelperText error sx={{ px: 2, textTransform: 'capitalize' }}>
                                                {error?.message}
                                            </FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Box>
                        <FormGroup sx={{ gap: 2 }}>
                            <FormControlLabel
                                key={1}
                                control={
                                    <Controller
                                        control={control}
                                        name={'isRegister'}
                                        render={({ field }) => (
                                            <Checkbox
                                                onChange={e => {
                                                    field.onChange(e.target.checked);
                                                }}
                                                checked={field.value}
                                            />
                                        )}
                                    />
                                }
                                label={'Is registered patient'}
                                sx={{ width: 'fit-content' }}
                            />
                        </FormGroup>
                        <MarketingPreferences lead={leadDetail} onUpdate={(update) => {
                            Object.entries(update).forEach(([key, value]) => setValue(key, value));
                        }} />
                        <Typography variant="h6" sx={{ mt: 3 }}>
                            Medical & Emergency
                        </Typography>
                        <Divider sx={{ marginBottom: '20px' }} />
                        <Box sx={fieldBoxStyle}>
                            <RHFTextField name="nhs_number" label="NHS Number" {...piiProps} />
                            <RHFTextField name="emergencyContactName" label="Emergency Contact Name" {...piiProps} />
                            <RHFTextField
                                name="emergencyContactPhone"
                                {...piiProps}
                                label="Emergency Contact Phone"
                                type="string"
                                inputProps={{ maxLength: 15 }}
                                onKeyDown={(evt) => {
                                    const isControlC = (evt.ctrlKey && evt.key === 'c') || (evt.metaKey && evt.key === 'c');
                                    const isControlV = (evt.ctrlKey && evt.key === 'v') || (evt.metaKey && evt.key === 'v');
                                    const isAllowedKey = inputKeys.includes(evt.key) || (/^[0-9]*$/).test(evt.key);
                                    if (!(isControlC || isControlV || isAllowedKey)) {
                                        evt.preventDefault();
                                    }
                                }}
                            />
                        </Box>
                        <Typography variant="h6" sx={{ mt: 3 }}>
                            Address
                        </Typography>
                        <Divider sx={{ marginBottom: '30px' }} />
                        <Box sx={autocompleteBoxStyle}>
                            <Controller
                                name="address"
                                control={control}
                                render={({ field }) => (
                                    <>
                                        <Iconify icon={'eva:search-outline'} sx={{
                                            position: 'absolute',
                                            left: '15px',
                                            top: '16px'

                                        }} color="grey.500" width={24} height={24} />
                                        <Autocomplete
                                            onPlaceSelected={(place, inputRef) => {
                                                const addresses = place.address_components;
                                                setAddressData(addresses, inputRef.value);
                                                field.onChange(inputRef.value);
                                            }}
                                            style={{
                                                paddingLeft: '45px',
                                                fontSize: '0.875rem'
                                            }}
                                            options={{
                                                types: ['establishment', 'geocode'],
                                                fields: ['address_components', 'formatted_address'],
                                                componentRestrictions: { country: 'uk' }
                                            }}
                                            defaultValue={field.value}
                                            className='address-input'
                                            placeholder='Search address'
                                        />
                                    </>
                                )}
                            />
                        </Box>
                        {getAddressField() && <Box sx={fieldBoxStyle}>
                            <RHFTextField name="address_line_1" label="Address line 1" {...piiProps} />
                            <RHFTextField name="address_line_2" label="Address line 2" {...piiProps} />
                            <RHFTextField name="city" label="City" {...piiProps} />
                            <RHFTextField name="county" label="County" {...piiProps} />
                            <RHFTextField name="postcode" label="Postcode" {...piiProps} />
                        </Box>}
                        <Typography variant="h6" sx={{ mt: 3 }}>
                            Lead Source
                        </Typography>
                        <Divider sx={{ marginBottom: '30px' }} />
                        <Box sx={selectBoxStyle}>
                            <RHFSelect
                                name='type'
                                label='Type'
                                value={values.type}
                                SelectProps={{ native: false, sx: selectStyle }}
                            >
                                {sourceType?.map((options, index) => (
                                    <MenuItem
                                        key={index}
                                        value={options.value}
                                        onChange={(e) => handleChange(e, 'type')}
                                        sx={menuItemStyle}
                                    >
                                        {options.label}
                                    </MenuItem>
                                ))}
                            </RHFSelect>
                            <ReferralDropdown
                                name={'referral'}
                                value={referralValue('value', values)[0]?.label || ''}
                                onChange={handleReferral}
                                affiliateUser={values?.affiliate_user ? { id: values?.affiliate_user } : leadDetail?.lead_source?.affiliate_user}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </FormProvider>
    );
});

LeadDetailsPersonalDetails.propTypes = {
    handleNext: PropTypes.func,
    type: PropTypes.string,
};

export default LeadDetailsPersonalDetails;